/*
  Click guard.

  On 15.10.2026 a rule of ours hid an app's popup by hiding the overlay inside
  its shadow root. The popup stopped being drawn, but the wrapper around it
  stayed: invisible, empty, and still taking every click on the page. Signed-in
  subscribers could not press anything for nineteen hours, and nobody reported
  it - a shopper who cannot click does not open a ticket, they leave, and the
  day just looks weak in the numbers.

  So the page checks itself. After things have settled it asks the document who
  is actually on top of the three controls that matter, and if the answer is
  something else it says so: a console warning for whoever is looking, and a
  GA4 event so it shows up in a report rather than waiting to be noticed.

  The hard part is not finding a cover - it is telling a fault from a drawer
  the shopper opened. The test is not whether the cover paints anything at that
  pixel: a real popup is transparent over most of the screen, and the point we
  check is a corner of it. The test is whether the cover has anything visible
  ANYWHERE inside it. A popup has a card; an abandoned wrapper has nothing.

  Nothing is shown to the shopper and nothing is blocked. This only watches.
*/
(function () {
  'use strict';

  var CONTROLS = [
    { name: 'add to cart', selectors: ['.my-addToCart-btn', '.product-form__submit[name="add"]'] },
    { name: 'checkout', selectors: ['#checkout', '#CartDrawer-Checkout'] },
    { name: 'header cart', selectors: ['#cart-icon-bubble'] }
  ];

  var MAX_PER_SESSION = 3;
  var SCAN_LIMIT = 400;
  var reported = {};

  function sessionCount() {
    try {
      return parseInt(sessionStorage.getItem('kds_click_guard') || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function countReport() {
    try {
      sessionStorage.setItem('kds_click_guard', String(sessionCount() + 1));
    } catch (e) {
      /* private mode, blocked storage - the check still works, it just cannot
         remember how often it has fired */
    }
  }

  function paints(el, cs) {
    var bg = cs.backgroundColor;
    if (bg && bg !== 'transparent' && bg.replace(/\s/g, '') !== 'rgba(0,0,0,0)') return true;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') return true;
    if (cs.boxShadow && cs.boxShadow !== 'none') return true;
    if (parseFloat(cs.borderTopWidth) > 0 || parseFloat(cs.borderBottomWidth) > 0) return true;

    var tag = el.tagName;
    if (tag === 'IMG' || tag === 'SVG' || tag === 'VIDEO' || tag === 'CANVAS' || tag === 'svg') return true;

    // Text counts, but only where there is a box to draw it in. A hidden
    // subtree still carries its text, and that is the case that fooled us.
    for (var i = 0; i < el.childNodes.length; i++) {
      var child = el.childNodes[i];
      if (child.nodeType === 3 && child.nodeValue && child.nodeValue.trim()) return true;
    }
    return false;
  }

  /**
   * Is there anything to see inside this cover? Walks its subtree and the
   * shadow roots inside it, stopping at the first thing with a box and some
   * paint. A drawer or a popup answers yes on its first card; a wrapper left
   * behind by a hidden element has nothing with a box at all.
   */
  function hasVisiblePaint(root) {
    var queue = [root];
    var seen = 0;

    while (queue.length && seen < SCAN_LIMIT) {
      var node = queue.shift();
      seen++;

      if (node.nodeType === 1) {
        var rect = node.getBoundingClientRect();
        if (rect.width > 1 && rect.height > 1) {
          var cs = getComputedStyle(node);
          if (cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0 && paints(node, cs)) return true;
        }
        if (node.shadowRoot) queue.push(node.shadowRoot);
      }

      var kids = node.children;
      if (kids) {
        for (var i = 0; i < kids.length; i++) queue.push(kids[i]);
      }
    }
    return false;
  }

  function describe(el) {
    if (!el) return 'nothing';
    var out = el.tagName.toLowerCase();
    if (el.id) out += '#' + el.id;
    if (el.className && typeof el.className === 'string') {
      out += '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.');
    }
    return out.slice(0, 120);
  }

  function findControl(control) {
    for (var i = 0; i < control.selectors.length; i++) {
      var el = document.querySelector(control.selectors[i]);
      if (el) return el;
    }
    return null;
  }

  function check(control) {
    if (reported[control.name]) return;

    var el = findControl(control);
    if (!el || el.disabled) return;

    var rect = el.getBoundingClientRect();
    if (rect.width < 4 || rect.height < 4) return;

    // Off screen cannot be hit-tested, and a control the shopper has scrolled
    // past is not one they are trying to press.
    var x = rect.left + rect.width / 2;
    var y = rect.top + rect.height / 2;
    if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return;

    // Everything stacked over the point, nearest first, down to the control.
    // elementsFromPoint stops at a shadow host, which is what we want here:
    // the host is the cover, and the whole of it gets judged together.
    var stack = document.elementsFromPoint(x, y);
    var covers = [];
    for (var i = 0; i < stack.length; i++) {
      var node = stack[i];
      if (node === el || el.contains(node) || node.contains(el)) break;
      covers.push(node);
    }
    if (!covers.length) return;

    var cover = covers[0];
    if (hasVisiblePaint(cover)) return;

    reported[control.name] = true;
    if (sessionCount() >= MAX_PER_SESSION) return;
    countReport();

    var blocker = describe(cover);
    if (window.console && console.warn) {
      console.warn(
        '[click-guard] "' + control.name + '" is covered by ' + blocker + ' - nothing visible inside it, and it is taking the click.',
        { control: el, blocker: cover }
      );
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'blocked_control', {
        control: control.name,
        blocker: blocker,
        page: window.location.pathname.slice(0, 100)
      });
    }
  }

  function run() {
    for (var i = 0; i < CONTROLS.length; i++) {
      try {
        check(CONTROLS[i]);
      } catch (e) {
        // A diagnostic must never be the thing that breaks the page.
      }
    }
  }

  // Twice: once the page has settled, and once more after the apps that arrive
  // late have had their turn - the popup that caused all this shows at ten
  // seconds.
  function schedule() {
    setTimeout(run, 3000);
    setTimeout(run, 12000);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);

  // The cart redrawing is when a cover is most likely to be left behind.
  document.addEventListener('cart:refreshed', function () {
    setTimeout(run, 600);
  });
})();
